import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Radio, Clock, Users, AlertTriangle, CheckCircle, XCircle, TrendingUp, User, BarChart3, Podcast } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  healthy: {
    color: 'emerald',
    bgClass: 'bg-emerald-500/10 border-emerald-500/30',
    dotClass: 'bg-emerald-500',
    textClass: 'text-emerald-400',
    label: 'Active',
    icon: CheckCircle,
  },
  partial: {
    color: 'yellow',
    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
    dotClass: 'bg-yellow-500',
    textClass: 'text-yellow-400',
    label: 'Partial',
    icon: AlertTriangle,
  },
  degraded: {
    color: 'orange',
    bgClass: 'bg-orange-500/10 border-orange-500/30',
    dotClass: 'bg-orange-500',
    textClass: 'text-orange-400',
    label: 'Degraded',
    icon: AlertTriangle,
  },
  failing: {
    color: 'red',
    bgClass: 'bg-red-500/10 border-red-500/30',
    dotClass: 'bg-red-500',
    textClass: 'text-red-400',
    label: 'Failing',
    icon: XCircle,
  },
};

export const ListenerSamplingPulse = ({ token }) => {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(false);

  const fetchPulse = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stream-monitor/listener-sampling/pulse`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setPulse(await res.json());
        setError(false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  }, [token]);

  useEffect(() => {
    fetchPulse();
    const interval = setInterval(fetchPulse, 20000);
    return () => clearInterval(interval);
  }, [fetchPulse]);

  if (error || !pulse) {
    return (
      <div className="bg-gray-800/40 border border-gray-700/40 rounded-lg px-3 py-2 flex items-center gap-2 text-xs text-gray-500" data-testid="sampling-pulse-offline">
        <Radio className="w-3.5 h-3.5" />
        <span>Listener Sampling: --</span>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[pulse.status] || STATUS_CONFIG.failing;
  const StatusIcon = cfg.icon;
  const lastAgo = pulse.last_sample_seconds_ago;
  const lastAgoText = lastAgo != null
    ? lastAgo < 60 ? `${lastAgo}s` : `${Math.floor(lastAgo / 60)}m`
    : '--';

  return (
    <div className={`border rounded-lg px-3 py-2.5 ${cfg.bgClass}`} data-testid="sampling-pulse">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {/* Status indicator */}
        <div className="flex items-center gap-2" data-testid="sampling-pulse-status">
          <span className="relative flex h-2.5 w-2.5">
            {pulse.status === 'healthy' && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.dotClass} opacity-75`} />
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.dotClass}`} />
          </span>
          <span className={`text-xs font-semibold ${cfg.textClass} uppercase tracking-wider`}>
            Listener Sampling
          </span>
          <StatusIcon className={`w-3.5 h-3.5 ${cfg.textClass}`} />
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-4 bg-gray-700" />

        {/* Stations breakdown: Live Audience + Stream Analytics */}
        <div className="flex items-center gap-3 text-xs" data-testid="sampling-pulse-stations">
          {/* Live Audience stations */}
          <div className="flex items-center gap-1" data-testid="sampling-pulse-live" title="Live Audience stations recording">
            <Podcast className="w-3.5 h-3.5 text-emerald-400" />
            <span className={`font-bold ${cfg.textClass}`}>{pulse.live_recording ?? '–'}</span>
            <span className="text-gray-600">/ {pulse.live_total ?? '–'}</span>
          </div>
          {/* Stream Analytics stations */}
          <div className="flex items-center gap-1" data-testid="sampling-pulse-stream" title="Stream Analytics stations recording">
            <BarChart3 className="w-3.5 h-3.5 text-purple-400" />
            <span className={`font-bold ${cfg.textClass}`}>{pulse.stream_recording ?? '–'}</span>
            <span className="text-gray-600">/ {pulse.stream_total ?? '–'}</span>
          </div>
        </div>

        {/* Samples this hour */}
        <div className="flex items-center gap-1.5 text-xs" data-testid="sampling-pulse-samples">
          <TrendingUp className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-gray-300 font-medium">{pulse.total_samples.toLocaleString('el-GR')}</span>
          <span className="text-gray-600">samples</span>
        </div>

        {/* Connected listeners */}
        {pulse.avg_listeners > 0 && (
          <div className="flex items-center gap-1.5 text-xs" data-testid="sampling-pulse-listeners">
            <User className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-blue-400 font-medium">{pulse.avg_listeners.toLocaleString('el-GR')}</span>
            <span className="text-gray-600">avg</span>
            {pulse.global_peak > 0 && (
              <>
                <span className="text-gray-700">|</span>
                <span className="text-rose-400 font-medium">{pulse.global_peak.toLocaleString('el-GR')}</span>
                <span className="text-gray-600">peak</span>
              </>
            )}
          </div>
        )}

        {/* Last sample time */}
        <div className="flex items-center gap-1.5 text-xs" data-testid="sampling-pulse-last">
          <Clock className="w-3.5 h-3.5 text-gray-500" />
          <span className={lastAgo != null && lastAgo > 120 ? 'text-red-400' : 'text-gray-400'}>
            {lastAgoText} ago
          </span>
        </div>

        {/* Current hour */}
        <div className="flex items-center gap-1 text-xs text-gray-600" data-testid="sampling-pulse-hour">
          <span>{pulse.current_hour}</span>
          <span>+{pulse.minutes_elapsed}m</span>
        </div>

        {/* Previous hour comparison */}
        {pulse.previous_hour && pulse.previous_hour.total_samples > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-600" data-testid="sampling-pulse-prev">
            <span className="text-gray-700">|</span>
            <span>prev: {pulse.previous_hour.stations_with_data} st.</span>
            <span>{pulse.previous_hour.total_samples} smp</span>
          </div>
        )}
      </div>
    </div>
  );
};
